import { Flex } from "@mantine/core";
import { useEffect, useRef, useState } from "react";

import { OVERLAY_PREF } from "../constants";
import { useInputProps } from "../hooks/useInputProps";
import useNMEAAutomated from "../hooks/useNMEAAutomated";
import CoordinateCell from "./CoordinateCell";
import { getValueInFormat } from "./Coorinates.utils";

interface ICoordinates {
  isLatitude: boolean;
  isLongitude: boolean;
  isLong3?: boolean;
  isLat3?: boolean;
  value: string;
  id: string;
  exclusive?: string;
  required?: boolean;
  ambPreviousData?: string;
  sectionIndex?: number;
  source?: "auto" | "manual";
  name?: string;
  config?: [number, number, number];
}

const Coordinates = ({
  isLatitude,
  isLongitude,
  value,
  id,
  isLong3,
  isLat3,
  exclusive,
  required,
  name,
  source,
  sectionIndex,
  config,
}: ICoordinates) => {
  const validateCoordinates = (v: string) => {
    if (id.includes(OVERLAY_PREF)) return true;
    let valid = true;
    getValueInFormat(
      v,
      isLongitude,
      isLatitude,
      Boolean(isLong3),
      Boolean(isLat3),
      Boolean(isLong3 || isLat3),
      config,
    ).forEach((el: string) => {
      if (el.length < 1 && required) valid = false;
    });
    return valid;
  };

  const {
    onChange,
    value: fieldValue,
    error,
    disabled,
  } = useInputProps(
    id,
    value,
    id.includes(OVERLAY_PREF) ? false : required === true,
    id.includes("-exclusive-") ? true : exclusive,
    validateCoordinates,
  );
  const field = { value: fieldValue, disabled };

  const splitted = getValueInFormat(
    field.value,
    isLongitude,
    isLatitude,
    Boolean(isLong3),
    Boolean(isLat3),
    Boolean(isLong3 || isLat3),
    config,
  );
  const len1 = config?.[0] ?? (isLatitude ? 2 : 3);
  const len2 = config?.[1] ?? (isLong3 || isLat3 ? 2 : 3);
  const len3 = config?.[2] ?? (isLong3 || isLat3 ? 2 : 3);

  const miw1 = config ? 22 + len1 * 10 : isLatitude ? 42 : 52;
  const miw2 = config ? 22 + len2 * 10 : 52;
  const miw3 = config ? 22 + len3 * 10 : 52;

  const { handleNMEAChange } = useNMEAAutomated({
    id,
    source,
    sectionIndex,
    defaultValue: value,
    name,
  });
  const [number1, setNumber1] = useState<number | string>(splitted[0]);
  const [number2, setNumber2] = useState<number | string>(splitted[1]);
  const [number3, setNumber3] = useState<number | string>(splitted[2]);

  const number1Ref = useRef<HTMLInputElement | null>(null);
  const number2Ref = useRef<HTMLInputElement | null>(null);
  const number3Ref = useRef<HTMLInputElement | null>(null);

  const [isTouched, setIsTouched] = useState(false);

  useEffect(() => {
    setNumber1(splitted[0]);
    setNumber2(splitted[1]);
    setNumber3(splitted[2]);
  }, [splitted]);

  useEffect(() => {
    if (
      number1?.toString().length === len1 &&
      number2Ref.current &&
      isTouched
    ) {
      number2Ref.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as they were; changing them changes when this runs
  }, [number1, len1]);

  useEffect(() => {
    if (
      number2?.toString().length === len2 &&
      number3Ref.current &&
      isTouched
    ) {
      number3Ref.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as they were; changing them changes when this runs
  }, [number2, len2]);

  const handleInputChange = (value: string, lp: number) => {
    setIsTouched(true);
    let newValue = "";
    switch (lp) {
      case 1:
        newValue = `${value} ${number2} ${number3}`;
        break;
      case 2:
        newValue = `${number1} ${value} ${number3}`;
        break;
      case 3:
        newValue = `${number1} ${number2} ${value}`;
        break;
    }
    onChange(newValue);
    handleNMEAChange(newValue);
  };
  return (
    <Flex>
      <CoordinateCell
        lp={1}
        id={id}
        value={number1}
        numberRef={number1Ref}
        onChange={handleInputChange}
        setNumber={setNumber1}
        min={0}
        max={isLatitude ? 90 : 180}
        miw={miw1}
        maxLength={len1}
        disabled={field.disabled}
        error={error && !number1}
        required={required}
      />

      <CoordinateCell
        lp={2}
        id={id}
        value={number2}
        numberRef={number2Ref}
        onChange={handleInputChange}
        setNumber={setNumber2}
        min={0}
        max={59}
        miw={miw2}
        maxLength={len2}
        ml={2}
        mr={2}
        disabled={field.disabled}
        error={error && !number2}
        required={required}
      />

      <CoordinateCell
        lp={3}
        id={id}
        value={number3}
        numberRef={number3Ref}
        onChange={handleInputChange}
        setNumber={setNumber3}
        min={0}
        max={59}
        miw={miw3}
        maxLength={len3}
        disabled={field.disabled}
        error={error && !number3}
        required={required}
      />
    </Flex>
  );
};

export default Coordinates;
